/**
 * @fileoverview Auto-store authenticated users in Convex
 *
 * This component automatically calls the users.store mutation when a user
 * signs in with Clerk, ensuring their data is synced to Convex.
 *
 * @module components/StoreUserEffect
 */

import { useEffect } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useMutation } from 'convex/react'
import { api } from 'convex/_generated/api'
import { usePostHog } from '@/hooks/usePostHog'

/**
 * StoreUserEffect Component
 *
 * Listens for Clerk authentication state changes and automatically
 * stores/updates the user in Convex when they sign in.
 *
 * This ensures that:
 * 1. New users are created in Convex after signing up
 * 2. Existing users have their data updated
 * 3. The `users.current` query can find the authenticated user
 *
 * Usage: Add this component once at the root of your app
 */
export function StoreUserEffect() {
  const { isSignedIn, userId } = useAuth()
  const storeUser = useMutation(api.users.store)
  const { trackEvent } = usePostHog()

  useEffect(() => {
    // Only store if user is signed in
    if (!isSignedIn || !userId) {
      return
    }

    // Store user in Convex
    const store = async () => {
      try {
        const result = await storeUser({})
        console.log('[Auth] User stored in Convex successfully')

        // Track user signup for new users
        if (result.isNewUser) {
          trackEvent('user_signed_up')
          console.log('[Auth] New user signup tracked')
        }
      } catch (error) {
        console.error('[Auth] Failed to store user:', error)
      }
    }

    store()
  }, [isSignedIn, userId, storeUser, trackEvent])

  // This component doesn't render anything
  return null
}
